import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import {
    Breadcrumb,
    Button,
    Table,
    Modal,
    Form,
    Input,
    InputNumber,
    Switch,
    Space,
    Tag,
    Tooltip,
    Typography,
    Popconfirm
} from 'antd';
import {
    SettingOutlined,
    PlusOutlined,
    EditOutlined,
    DeleteOutlined
} from '@ant-design/icons';
import { SetRoute } from '../scripts/action';
import Resources from '../scripts/resources';
import API from '../scripts/api';

const { Text } = Typography;

class AiPromptSetting extends Component {
    constructor(props) {
        super(props);

        this.state = {
            data: [],
            loading: false,
            visible: false,
            editItem: null,
            form: this.getEmptyForm()
        };
    }

    getEmptyForm = () => ({
        key: '',
        title: '',
        model: '',
        system_prompt: '',
        user_prompt: '',
        temperature: 0,
        max_tokens: 0,
        is_active: false
    });

    componentDidMount() {
        const { onSetRoute, routeName, parentName } = this.props;

        onSetRoute({ openKey: parentName, selectedKey: routeName });
        this.fetch();
    }

    fetch = async () => {
        this.setState({ loading: true });

        try {
            const result = await API.aiPromptSetting.list();
            this.setState({
                data: result.data || result || [],
                loading: false
            });
        } catch (error) {
            this.setState({ loading: false });
        }
    };

    showModal = (item = null) => {
        this.setState({
            visible: true,
            editItem: item,
            form: item
                ? {
                      key: item.key || '',
                      title: item.title || '',
                      prompt: item.prompt || '',
                      is_active: !!item.is_active
                  }
                : this.getEmptyForm()
        });
    };

    hideModal = () => {
        this.setState({
            visible: false,
            editItem: null,
            form: this.getEmptyForm()
        });
    };

    onChange = (name, value) => {
        this.setState((prevState) => ({
            form: {
                ...prevState.form,
                [name]: value
            }
        }));
    };

    submit = async () => {
        const { form, editItem } = this.state;

        this.setState({ loading: true });

        try {
            if (editItem) {
                await API.aiPromptSetting.update(editItem.id, form);
            } else {
                await API.aiPromptSetting.create(form);
            }

            this.setState({
                loading: false,
                visible: false,
                editItem: null,
                form: this.getEmptyForm()
            });

            this.fetch();
        } catch (error) {
            this.setState({ loading: false });
        }
    };

    deleteItem = async (id) => {
        this.setState({ loading: true });

        try {
            await API.aiPromptSetting.delete(id);
            this.setState({ loading: false });
            this.fetch();
        } catch (error) {
            this.setState({ loading: false });
        }
    };

    renderPromptPreview = (text) => {
        if (!text) return <Text type="secondary">-</Text>;

        const shortText = text.length > 50 ? `${text.slice(0, 50)}...` : text;

        return (
            <Tooltip title={text}>
                <Text>{shortText}</Text>
            </Tooltip>
        );
    };

    render() {
        const { data, loading, visible, form, editItem } = this.state;

        const columns = [
            {
                title: Resources.key || 'Key',
                dataIndex: 'key',
                key: 'key',
                render: (value) => <Tag color="blue">{value || '-'}</Tag>
            },
            {
                title: Resources.title || 'Title',
                dataIndex: 'title',
                key: 'title',
                render: (value) => value || '-'
            },
          
            {
                title: 'Prompt',
                dataIndex: 'prompt',
                key: 'prompt',
                render: this.renderPromptPreview
            },
            {
                title: Resources.status || 'Status',
                dataIndex: 'is_active',
                key: 'is_active',
                align: 'center',
                render: (value) =>
                    value ? <Tag color="green">Active</Tag> : <Tag>Inactive</Tag>
            },
            {
                title: 'Action',
                key: 'action',
                align: 'center',
                render: (_, record) => (
                    <Space size="small">
                        <Button
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => this.showModal(record)}
                        >
                            Edit
                        </Button>

                        <Popconfirm
                            title="Are you sure you want to delete this item?"
                            onConfirm={() => this.deleteItem(record.id)}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button danger size="small" icon={<DeleteOutlined />}>
                                Delete
                            </Button>
                        </Popconfirm>
                    </Space>
                )
            }
        ];

        return (
            <Fragment>
                <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
                    <Breadcrumb>
                        <Breadcrumb.Item>
                            <SettingOutlined />
                            <span style={{ marginLeft: 6 }}>{Resources.aiPromptSetting || 'AI Prompt Settings'}</span>
                        </Breadcrumb.Item>
                    </Breadcrumb>

                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => this.showModal()}
                    >
                        {Resources.aiPromptSetting || 'AI Prompt Setting'}
                    </Button>
                </Space>

                <div className="ant-content">
                    <Table
                        rowKey="id"
                        columns={columns}
                        dataSource={data}
                        loading={loading}
                        pagination={{ pageSize: 10 }}
                        bordered
                        size="middle"
                        scroll={{ x: 1200 }}
                    />
                </div>

                <Modal
                    title={editItem ? 'Edit AI Prompt Setting' : 'Create AI Prompt Setting'}
                    open={visible}
                    onCancel={this.hideModal}
                    onOk={this.submit}
                    confirmLoading={loading}
                    okText={editItem ? 'Update' : 'Create'}
                    cancelText="Cancel"
                    width={800}
                    destroyOnClose
                >
                    <Form layout="vertical">
                        <Form.Item label={Resources.key || 'Key'}>
                            <Input
                                value={form.key}
                                onChange={(e) => this.onChange('key', e.target.value)}
                                placeholder="example: extract_filters"
                            />
                        </Form.Item>

                        <Form.Item label={Resources.title || 'Title'}>
                            <Input
                                value={form.title}
                                onChange={(e) => this.onChange('title', e.target.value)}
                                placeholder="Example: Extract Filters Prompt"
                            />
                        </Form.Item>

                       

                        <Form.Item label="Prompt">
                            <Input.TextArea
                                rows={5}
                                value={form.prompt}
                                onChange={(e) => this.onChange('prompt', e.target.value)}
                                placeholder="Write system instructions here..."
                            />
                        </Form.Item>

                     

                        <Form.Item label={Resources.status || 'Status'} style={{ marginTop: 16 }}>
                            <Switch
                                checked={form.is_active}
                                onChange={(checked) => this.onChange('is_active', checked)}
                                checkedChildren="Active"
                                unCheckedChildren="Inactive"
                            />
                        </Form.Item>
                    </Form>
                </Modal>
            </Fragment>
        );
    }
}

export default connect(
    null,
    (dispatch) => ({
        onSetRoute: (data) => dispatch(SetRoute(data))
    })
)(AiPromptSetting);
